<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_quote_title') }}
        </h4>
        <b-button
          v-if="hasPermission('create invoices')"
          variant="primary"
          size="sm"
          class="rounded-pill"
          :to="{ name: 'ledger-quotes-new' }"
        >
          {{ $t('ledger_quote_new') }}
        </b-button>
      </div>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_quote_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(document_number)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.document_number }}
              </b-link>
            </template>
            <template #cell(gl_account)="data">
              {{ accountLabel(data.item.gl_account) }}
            </template>
            <template #cell(total)="data">
              <span class="ledger-num">{{ money(data.item.total) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item)">
                {{ statusLabel(data.item) }}
              </b-badge>
              <b-badge
                v-if="data.item.is_locked"
                variant="dark"
                class="ml-50"
              >
                {{ $t('ledger_quote_locked') }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-quote-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.document_number }}</strong>
              <b-badge :variant="statusVariant(item)">
                {{ statusLabel(item) }}
              </b-badge>
            </div>
            <div>{{ item.payment_mode }}</div>
            <div class="ledger-num">
              {{ money(item.total) }}
            </div>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 15"
          @change="onPage"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BTable, BBadge, BLink, BSpinner, BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getQuotes } from '@/api/accounting/quotes'
import {
  listFrom, formatLedgerMoney, accountOptionLabel, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'QuoteList',
  components: {
    BCard, BButton, BTable, BBadge, BLink, BSpinner, BPagination,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      page: 1,
    }
  },
  computed: {
    fields() {
      return [
        { key: 'document_number', label: this.$t('ledger_quote_number') },
        { key: 'quote_date', label: this.$t('ledger_date') },
        { key: 'type', label: this.$t('ledger_type') },
        { key: 'payment_mode', label: this.$t('ledger_quote_payment_mode') },
        { key: 'gl_account', label: this.$t('ledger_account') },
        { key: 'total', label: this.$t('ledger_inv_total') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    accountLabel(account) {
      return account ? accountOptionLabel(account, this.$i18n.locale) : '—'
    },
    statusLabel(item) {
      return item.status === 'posted' ? this.$t('ledger_quote_posted') : this.$t('ledger_quote_draft')
    },
    statusVariant(item) {
      return item.status === 'posted' ? 'success' : 'secondary'
    },
    showRoute(item) {
      return { name: 'ledger-quotes-view', params: { id: item.id } }
    },
    onPage(page) {
      this.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const res = await getQuotes({ page: this.page, per_page: 15 })
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-quote-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
</style>
